import React, { useState, useEffect, useRef } from 'react';
import { Icons } from '../Icons';
import { ncaaSchools as fallbackSchools } from '../../data/ncaaSchools';

const SchoolAutocomplete = ({ value, onChange, placeholder = "Search for your school...", className = "" }) => {
  const [inputValue, setInputValue] = useState(value || '');
  const [schools, setSchools] = useState(fallbackSchools || []);
  const [filteredOptions, setFilteredOptions] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const dropdownRef = useRef(null);

  // Load schools from backend
  useEffect(() => {
    const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL || window.location.origin;
    fetch(`${backendUrl}/api/schools/ncaa`)
      .then(res => res.json())
      .then(data => {
        if (data?.schools?.length > 0) {
          setSchools(data.schools);
        }
      })
      .catch(() => setSchools(fallbackSchools));
  }, []);

  // Update input when value prop changes
  useEffect(() => {
    setInputValue(value || '');
  }, [value]);

  // Filter schools when input changes
  useEffect(() => {
    if (!isFocused || !inputValue || inputValue.length === 0) {
      setFilteredOptions([]);
      setShowDropdown(false);
      return;
    }

    const searchWords = inputValue.toLowerCase().trim().split(/\s+/);
    const filtered = schools
      .filter(school => {
        const lc = school.toLowerCase();
        return searchWords.every(word => lc.includes(word));
      })
      .slice(0, 10);

    setFilteredOptions(filtered);
    setShowDropdown(filtered.length > 0);
  }, [inputValue, schools, isFocused]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    onChange(newValue);
  };

  const handleSelectSchool = (school) => {
    setInputValue(school);
    onChange(school);
    setShowDropdown(false);
  };

  return (
    <div className="relative w-full" ref={dropdownRef}>
      <div className="relative">
        <input
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setTimeout(() => setIsFocused(false), 150)}
          placeholder={placeholder}
          className={`input-field pr-10 ${className}`}
        />
        <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
          <Icons.Search className="w-5 h-5 text-text-muted" />
        </div>
      </div>

      {showDropdown && (
        <div style={{ position: 'absolute', zIndex: 9999, width: '100%', marginTop: 4, background: '#1a1a2e', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 10, boxShadow: '0 12px 40px rgba(0,0,0,0.7)', maxHeight: 240, overflowY: 'auto' }}>
          {filteredOptions.map((school, index) => (
            <button
              key={index}
              onClick={() => handleSelectSchool(school)}
              style={{ width: '100%', padding: '10px 14px', textAlign: 'left', color: 'rgba(255,255,255,0.85)', background: 'none', border: 'none', borderBottom: '1px solid rgba(255,255,255,0.05)', cursor: 'pointer', fontSize: '0.85rem', transition: 'background 100ms ease' }}
              onMouseEnter={e => e.currentTarget.style.background = 'rgba(90,56,253,0.12)'}
              onMouseLeave={e => e.currentTarget.style.background = 'none'}
            >
              {school}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default SchoolAutocomplete;
